import { useState, useCallback, useEffect } from "react";
import { useJobPolling } from "./useJobPolling";
import { usePost } from "./usePost";
import { useToast } from "../store/ToastProvider";
import { AIModalState } from "../enums/CompassAI";
import {
  BatchAssessEngagementRequest,
  BatchAssessmentResult,
  AIJobProgress,
  AIFileDownloadRequest,
} from "../types/compassAITypes";
import { MESSAGES } from "../messages";

// =============================================================================
// TYPES
// =============================================================================

interface UseEngagementAssessmentReturn {
  state: AIModalState;
  assessmentResult: BatchAssessmentResult | null;
  errorMessage: string | null;
  progress: AIJobProgress | null;
  startAssessment: (payload: BatchAssessEngagementRequest) => Promise<void>;
  downloadFile: (path: string) => Promise<string | null>;
  reset: () => void;
  isAnalyzing: boolean;
  isPolling: boolean;
  isComplete: boolean;
  hasError: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

export function useEngagementAssessment(): UseEngagementAssessmentReturn {
  const { addToast } = useToast();

  const [state, setState] = useState<AIModalState>(AIModalState.IDLE);
  const [assessmentResult, setAssessmentResult] =
    useState<BatchAssessmentResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { sendData: getDownloadUrl } = usePost<
    { url: string },
    AIFileDownloadRequest
  >();

  const {
    startJob,
    reset: resetPolling,
    progress,
    result: jobResult,
    errorMessage: jobError,
    isPolling,
    isComplete: jobComplete,
    isFailed: jobFailed,
  } = useJobPolling<BatchAssessmentResult>();

  // ── Track polling state ────────────────────────────────────

  useEffect(() => {
    if (isPolling && state !== AIModalState.POLLING) {
      setState(AIModalState.POLLING);
    }
  }, [isPolling]);

  useEffect(() => {
    if (jobComplete && jobResult) {
      setAssessmentResult(jobResult);
      setState(AIModalState.SUCCESS);
    }
  }, [jobComplete, jobResult]);

  useEffect(() => {
    if (jobFailed) {
      setState(AIModalState.ERROR);
      const msg = jobError || MESSAGES.COMPASS_AI.ERRORS.ASSESSMENT_FAILED;
      setErrorMessage(msg);
      addToast(MESSAGES.COMPASS_AI.ERRORS.ASSESSMENT_FAILED, "error");
    }
  }, [jobFailed]);

  // ── Actions ────────────────────────────────────────────────

  const startAssessment = useCallback(
    async (payload: BatchAssessEngagementRequest): Promise<void> => {
      setState(AIModalState.ANALYZING);
      setErrorMessage(null);

      await startJob(
        "AI_ASSESS_ENGAGEMENTS_BATCH",
        payload as unknown as Record<string, unknown>,
      );
    },
    [startJob],
  );

  const downloadFile = useCallback(
    async (path: string): Promise<string | null> => {
      const response = await getDownloadUrl("FILE_DOWNLOAD", { path });

      if (response?.url) {
        return response.url;
      } else {
        addToast(MESSAGES.COMPASS_AI.ERRORS.DOWNLOAD_FAILED, "error");
        return null;
      }
    },
    [getDownloadUrl, addToast],
  );

  const reset = useCallback((): void => {
    setState(AIModalState.IDLE);
    setAssessmentResult(null);
    setErrorMessage(null);
    resetPolling();
  }, [resetPolling]);

  return {
    state,
    assessmentResult,
    errorMessage,
    progress,
    startAssessment,
    downloadFile,
    reset,
    isAnalyzing:
      state === AIModalState.ANALYZING || state === AIModalState.POLLING,
    isPolling: state === AIModalState.POLLING,
    isComplete: state === AIModalState.SUCCESS,
    hasError: state === AIModalState.ERROR,
  };
}
